import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Table, Tag, TableColumnsType, Tooltip } from "antd";
import { LinkOutlined, SettingOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSlack } from "@fortawesome/free-brands-svg-icons";

import { ProjectWithChildren } from "../../bitmaker/schemas/Projects/ProjectWithChildren";
import { REDMINE_URL } from "../../constants";
import { useProjects } from "../../hooks/useProjects";
import { useAccounts } from "../../hooks/useAccounts";
import { useActiveUsers } from "../../hooks/useActiveUsers";
import { useFilteredProjects } from "../../hooks/useFilteredProjects";

export default function ProjectsDashboard() {
  const { projects, loadingProjects, fetchProjectsError, tagsFilterList } =
    useProjects();
  const { accounts: accountsFilterList } = useAccounts();
  const { users: ownerFilterList } = useActiveUsers();

  const cleanedProjects = useMemo(() => {
    const clean = (items: ProjectWithChildren[]): ProjectWithChildren[] =>
      items.map((item) => {
        if (!item.children || item.children.length === 0) {
          const { children, ...rest } = item;
          return rest;
        }

        return {
          ...item,
          children: clean(item.children),
        };
      });

    return clean(projects ?? []);
  }, [projects]);

  const [tableFilters, setTableFilters] = useState<any>({});

  const normalize = useCallback((value: any) => {
    if (!value || value.length === 0) return undefined;
    return value;
  }, []);

  const normalizedFilters = useMemo(() => {
    return {
      account: normalize(tableFilters.account),
      assignee: normalize(tableFilters.assignee),
      tags: normalize(tableFilters.tags),
    };
  }, [tableFilters, normalize]);

  const { filteredProjects, expandedRowKeys } = useFilteredProjects(
    cleanedProjects,
    normalizedFilters,
  );

  const hasActiveFilters =
    normalizedFilters.account ||
    normalizedFilters.assignee ||
    normalizedFilters.tags;

  const highlight = useCallback(
    (value: any, isActive: boolean) => {
      if (!hasActiveFilters || !isActive) return value;

      return <span className="bg-yellow-100/60 px-1 rounded-sm">{value}</span>;
    },
    [hasActiveFilters],
  );

  const projectColumns = useMemo<TableColumnsType<ProjectWithChildren>>(
    () => [
      {
        key: "id",
        title: "ID",
        dataIndex: "id",
        align: "left",
        width: 120,
        render: (_, record) => {
          const childrenCount =
            Array.isArray(record.children) && record.children.length > 0
              ? record.children.length
              : 0;

          return (
            <div className="inline-flex items-center gap-2">
              <Tooltip title="Open in Redmine">
                <a
                  href={`${REDMINE_URL}/issues/${record.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold link-primary"
                >
                  {record.id}
                </a>
              </Tooltip>

              {childrenCount > 0 && (
                <span className="badge-muted">{childrenCount}</span>
              )}
            </div>
          );
        },
      },
      {
        key: "account",
        title: "ACCOUNT",
        dataIndex: "account",
        align: "center",
        width: 150,
        render: (_, record) => {
          const isActive = normalizedFilters.account?.includes(
            record.account ?? "",
          );

          return <span>{highlight(record.account ?? "—", isActive)}</span>;
        },
      },
      {
        key: "assignee",
        title: "OWNER",
        dataIndex: "assignee",
        align: "center",
        width: 150,
        render: (_, record) => {
          const isActive = normalizedFilters.assignee?.includes(
            Number(record.assignee_id),
          );

          return <span>{highlight(record.assignee ?? "—", isActive)}</span>;
        },
      },
      {
        key: "subject",
        title: "NAME",
        dataIndex: "subject",
        align: "left",
        ellipsis: true,
        render: (_, record) => (
          <Link
            to={`/projects/${record.id}`}
            className="link-text truncate block max-w-full"
          >
            {record.subject}
          </Link>
        ),
      },
      {
        key: "tags",
        title: "TAGS",
        dataIndex: "tags",
        align: "left",
        width: 180,
        render: (_, { tags }) => {
          const tagColors: Record<string, string> = {
            ps: "purple",
            maintenance: "green",
            bi: "volcano",
          };

          if (!tags?.length) {
            return <span>—</span>;
          }

          return (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag: { name: string }) => {
                const color = tagColors[tag.name] ?? "geekblue";
                return (
                  <Tag color={color} key={tag.name}>
                    {tag.name}
                  </Tag>
                );
              })}
            </div>
          );
        },
      },
      {
        key: "redmine",
        title: "REDMINE TICKETS",
        dataIndex: "redmine",
        align: "center",
        width: 120,
        render: (_, record) => (
          <Tooltip title="Open in Redmine">
            <a
              href={`${REDMINE_URL}/issues/${record.id}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Open Redmine tickets"
              className="icon-button link-primary-icon"
            >
              <LinkOutlined className="text-base" />
            </a>
          </Tooltip>
        ),
      },
      {
        key: "contact",
        title: "CONTACT",
        dataIndex: "contact",
        align: "left",
        width: 240,
        render: (_, record) => {
          const slackChannels = record.bitmaker_settings?.slack?.channels;

          if (slackChannels?.length) {
            return (
              <div className="flex flex-col gap-1">
                {slackChannels.map((channel: string) => (
                  <Tooltip key={channel} title="Open in Slack">
                    <a
                      href={`https://bitmakerla.slack.com/channels/${channel}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-2 py-0.5 rounded text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      <FontAwesomeIcon icon={faSlack} />
                      <span>#{channel}</span>
                    </a>
                  </Tooltip>
                ))}
              </div>
            );
          }

          return (
            <Tooltip title="Configure in Redmine">
              <a
                href={`${REDMINE_URL}/issues/${record.id}`}
                target="_blank"
                rel="noreferrer"
                aria-label="Configure project settings"
                className="icon-button link-danger-icon"
              >
                <SettingOutlined className="text-base" />
              </a>
            </Tooltip>
          );
        },
      },
    ],
    [normalizedFilters, highlight],
  );

  const columnsWithFilters = useMemo(() => {
    return projectColumns.map((column) => {
      if (column.key === "account") {
        return {
          ...column,
          filters: accountsFilterList.map((account) => ({
            text: account.name,
            value: account.name,
          })),
        };
      }

      if (column.key === "assignee") {
        return {
          ...column,
          filters: ownerFilterList.map((dev) => ({
            text: dev.login,
            value: dev.id,
          })),
        };
      }

      if (column.key === "tags") {
        return {
          ...column,
          filters: tagsFilterList,
        };
      }

      return column;
    });
  }, [accountsFilterList, ownerFilterList, tagsFilterList, projectColumns]);

  return (
    <div className="w-full h-full flex px-12 py-8 bg-gray-50">
      <div className="w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-700 tracking-tight">
            Projects
          </h2>
        </div>

        {fetchProjectsError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
            {fetchProjectsError}
          </div>
        )}

        {/* Projects Table */}
        <div className="card-container">
          <h3 className="section-title">All Open Projects</h3>
          <Table
            columns={columnsWithFilters}
            dataSource={filteredProjects}
            onChange={(_, filters) => setTableFilters(filters)}
            size={"small"}
            className="text-sm"
            loading={loadingProjects}
            rowKey={(record) => record.id}
            rowClassName={(record: any) => {
              if (!hasActiveFilters) {
                return "hover:bg-gray-50";
              }

              if (record.isMatch) {
                return "bg-blue-50 hover:bg-blue-100";
              }

              return "hover:bg-gray-50";
            }}
            pagination={{
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            expandable={{
              childrenColumnName: "children",
              expandedRowKeys,
              expandRowByClick: true,
            }}
            scroll={{ x: 1300 }}
            bordered={false}
            tableLayout="fixed"
            sticky
          />
        </div>
      </div>
    </div>
  );
}
